import type { Column } from "@tanstack/react-table"

/**
 * Generates CSS styles for pinned columns.
 * Creates sticky positioning with shadow effects at pin boundaries.
 * 
 * Based on tablecn implementation.
 */
export function getCommonPinningStyles<TData>({
    column,
    withBorder = true,
    isHeader = false,
}: {
    column: Column<TData>
    withBorder?: boolean
    isHeader?: boolean
}): React.CSSProperties {
    const isPinned = column.getIsPinned()
    const isLastLeftPinnedColumn =
        isPinned === "left" && column.getIsLastColumn("left")
    const isFirstRightPinnedColumn =
        isPinned === "right" && column.getIsFirstColumn("right")

    return {
        boxShadow: withBorder
            ? isLastLeftPinnedColumn
                ? "-4px 0 4px -4px hsl(var(--border)) inset"
                : isFirstRightPinnedColumn
                    ? "4px 0 4px -4px hsl(var(--border)) inset"
                    : undefined
            : undefined,
        left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
        right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
        opacity: isPinned ? 0.97 : 1,
        position: isPinned ? "sticky" : "relative",
        background: isPinned ?  "hsl(var(--background))" : undefined,
        width: column.getSize(),
        // Prevent columns from shrinking below their defined size (prevents column overlap)
        minWidth: isPinned ? column.getSize() : column.columnDef.minSize,
        zIndex: isPinned ? 1 : undefined,
    }
}
